import { Routes } from '@angular/router';
import { adminCanActivate, adminCanMatch } from './core/admin/admin-guard';
import { engagementOwnerGuard } from './core/guards/engagement-owner-guard';
import { projectMemberGuard } from './core/guards/project-member-guard';

export const routes: Routes = [
  {
    path: 'projects/:id/finish-it',
    canActivate: [projectMemberGuard],
    loadComponent: () =>
      import('./features/finish-it/finish-it').then((m) => m.FinishIt),
    title: 'Finish It | Angularize',
  },
  {
    path: 'projects/:id/engagements',
    canActivate: [projectMemberGuard],
    loadComponent: () =>
      import('./features/engagements/engagements-list/engagements-list')
        .then((m) => m.EngagementsListPage),
    title: 'Project engagements | Angularize',
  },
  {
    path: 'projects/:id/board',
    canActivate: [projectMemberGuard],
    data: { view: 'buyer' },
    loadComponent: () =>
      import('./features/engagements/board/engagement-board/engagement-board')
        .then((m) => m.EngagementBoard),
    title: 'Project board | Angularize',
  },
  {
    path: 'projects/:id/team',
    canActivate: [projectMemberGuard],
    loadComponent: () =>
      import('./features/projects/project-team/project-team/project-team')
        .then((m) => m.ProjectTeamPage),
    title: 'Project team | Angularize',
  },
  {
    path: 'projects/:id',
    canActivate: [projectMemberGuard],
    loadComponent: () =>
      import('./features/project/project-new/project-new.component')
        .then((m) => m.ProjectNewComponent),
  },
  {
    path: 'engagements',
    loadComponent: () =>
      import('./features/engagements/engagements-list/engagements-list')
        .then((m) => m.EngagementsListPage),
    title: 'Your engagements | Angularize',
  },
  {
    path: 'engagements/:id/review',
    canActivate: [engagementOwnerGuard],
    loadComponent: () =>
      import('./features/engagements/engagement-review/engagement-review')
        .then((m) => m.EngagementReviewPage),
    title: 'Review the work | Angularize',
  },
  {
    path: 'engagements/:id',
    canActivate: [engagementOwnerGuard],
    loadComponent: () =>
      import('./features/engagements/engagement-status/engagement-status')
        .then((m) => m.EngagementStatusPage),
    title: 'Your engagement | Angularize',
  },
  {
    path: 'admin/engagements',
    canMatch: [adminCanMatch],
    canActivate: [adminCanActivate],
    loadComponent: () =>
      import('./features/admin/admin-engagements/admin-engagements')
        .then((m) => m.AdminEngagementsPage),
    title: 'Admin · Engagements | Angularize',
  },
  {
    path: 'admin/engagements/:id/tickets',
    canMatch: [adminCanMatch],
    canActivate: [adminCanActivate],
    loadComponent: () =>
      import('./features/admin/admin-tickets/admin-tickets/admin-tickets')
        .then((m) => m.AdminTicketsPage),
    title: 'Admin · Tickets | Angularize',
  },
  {
    path: 'admin/collaborators',
    canMatch: [adminCanMatch],
    canActivate: [adminCanActivate],
    loadComponent: () =>
      import('./features/admin/admin-collaborators/admin-collaborators/admin-collaborators')
        .then((m) => m.AdminCollaboratorsPage),
    title: 'Admin · Collaborators | Angularize',
  },
  {
    // Execution Partner application review queue. Deliberately guarded by
    // adminCanMatch/adminCanActivate — NOT partnerGuard — since applicants
    // being reviewed here aren't approved partners yet.
    path: 'admin/partner-applications',
    canMatch: [adminCanMatch],
    canActivate: [adminCanActivate],
    loadComponent: () =>
      import('./features/partners/admin/admin-partner-applications/admin-partner-applications')
        .then((m) => m.AdminPartnerApplicationsPage),
    title: 'Admin · Partner Applications | Angularize',
  },
  {
    path: 'admin/projects/:id/board',
    canMatch: [adminCanMatch],
    canActivate: [adminCanActivate],
    data: { view: 'team' },
    loadComponent: () =>
      import('./features/engagements/board/engagement-board/engagement-board')
        .then((m) => m.EngagementBoard),
    title: 'Admin · Board | Angularize',
  },
  // app.routes.ts — inside your admin/guarded routes
{
  path: 'admin/emails',
  loadComponent: () =>
    import('@app/features/admin/admin-emails/admin-emails').then((m) => m.AdminEmailsPage),
},
  { path: 'admin/console', redirectTo: 'admin/hub', pathMatch: 'full' },
  { path: 'admin', redirectTo: 'admin/hub', pathMatch: 'full' },
  {
    path: 'admin/hub',
    canMatch: [adminCanMatch],
    canActivate: [adminCanActivate],
    loadComponent: () =>
      import('./features/admin/admin-hub/admin-hub').then((m) => m.AdminHubPage),
    title: 'Admin · Hub | Angularize',
  },
  {
    path: 'checkout',
    loadComponent: () =>
      import('./features/checkout/checkout.component').then((m) => m.CheckoutComponent),
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'product-builder',
    loadComponent: () =>
      import('./features/product-builder/product-builder').then((m) => m.ProductBuilderComponent),
    title: 'Talk to a Product Builder | Angularize',
  },
  {
    // MUST come before the trailing path:'' marketing entry below — Angular's
    // default pathMatch is 'prefix', so an empty path matches every URL as a
    // prefix and would otherwise swallow this route first.
    path: 'execution-partners',
    loadChildren: () =>
      import('./features/partners/partner.routes').then((m) => m.PARTNER_ROUTES),
  },
  {
    // PUBLIC, no-login profile page — deliberately a SEPARATE top-level path
    // from 'execution-partners' (singular vs plural), not a child of
    // partner.routes.ts, since everything in that group other than
    // landing/apply/applied is either guarded or app-shell chrome.
    path: 'execution-partner/:slug',
    loadComponent: () =>
      import('./features/partners/public-profile/partner-public-profile/partner-public-profile')
        .then((m) => m.PartnerPublicProfile),
    title: 'Execution Partner | Angularize',
  },
  {
    path: '',
    loadChildren: () =>
      import('./features/marketing/marketing.routes').then((m) => m.MARKETING_ROUTES),
  },
];